/** #434 (SPEC-434 3.1): one version. Summary and the Rates table, filtered server-side. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  Input,
  LoadingSpinner,
  Pagination,
  Select,
} from '@/components/common';
import { commissionGridService, type GridRowFilters } from '@/services/commissionGrids';
import { getErrorMessage } from '@/services/api';
import { STATUS_LABELS, activeFilterCount, saveBlob, statusVariant } from './gridPages';

const PAGE_SIZE = 20;
const EMPTY: GridRowFilters = {};
const TEXT_FILTERS: { key: keyof GridRowFilters; label: string }[] = [
  { key: 'region', label: 'Region or RTO code' },
  { key: 'age', label: 'Vehicle age (years)' },
  { key: 'fuel', label: 'Fuel' },
  { key: 'cc', label: 'CC' },
  { key: 'gvw', label: 'Tonnage (kg)' },
  { key: 'search', label: 'Search' },
];

export function GridVersionPage() {
  const { id = '' } = useParams();
  const [draft, setDraft] = useState<GridRowFilters>(EMPTY);
  const [applied, setApplied] = useState<GridRowFilters>(EMPTY);
  const [page, setPage] = useState(1);

  const summary = useQuery({
    queryKey: ['commission-grid-version', id],
    queryFn: () => commissionGridService.getVersion(id),
  });
  const rows = useQuery({
    queryKey: ['commission-grid-rows', id, applied, page],
    queryFn: () => commissionGridService.rows(id, applied, page),
  });

  const apply = () => {
    setPage(1);
    setApplied(draft);
  };
  const reset = () => {
    setPage(1);
    setDraft(EMPTY);
    setApplied(EMPTY);
  };
  const exportCsv = async () => {
    try {
      saveBlob(await commissionGridService.rowsCsv(id, applied), 'grid-rows.csv');
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  if (summary.isLoading) return <LoadingSpinner />;
  if (summary.error || !summary.data) {
    return (
      <p role="alert" className="text-sm text-red-600">
        {summary.error ? getErrorMessage(summary.error) : 'Version not found.'}
      </p>
    );
  }
  const v = summary.data.version;
  const classes = Object.keys(summary.data.by_class);
  const total = rows.data?.count ?? 0;

  return (
    <div>
      <PageHeader
        title={`${v.grid_name} v${v.version_no}`}
        subtitle={`${v.effective_from} to ${v.effective_until ?? 'open'} - ${summary.data.row_count} rows`}
        breadcrumbs={[
          { label: 'Commissions', href: '/commissions' },
          { label: 'Grids', href: '/commissions/grids' },
          { label: v.grid_name, href: `/commissions/grids/${v.grid_id}` },
          { label: `v${v.version_no}` },
        ]}
        actions={
          <Badge variant={statusVariant(v.status)}>{STATUS_LABELS[v.status] ?? v.status}</Badge>
        }
      />
      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            id="f-lob"
            label="Class"
            value={draft.lob ?? ''}
            options={[
              { value: '', label: 'All classes' },
              ...classes.map((c) => ({ value: c, label: c })),
            ]}
            onChange={(e) => setDraft({ ...draft, lob: e.target.value })}
          />
          <Select
            id="f-pt"
            label="Policy type"
            value={draft.policy_type ?? ''}
            options={[
              { value: '', label: 'Any' },
              { value: 'comprehensive', label: 'Comprehensive' },
              { value: 'tp_only', label: 'Third party only' },
              { value: 'saod', label: 'Standalone OD' },
            ]}
            onChange={(e) => setDraft({ ...draft, policy_type: e.target.value })}
          />
          <Select
            id="f-basis"
            label="Premium basis"
            value={draft.basis ?? ''}
            options={[
              { value: '', label: 'Any' },
              { value: 'od_premium', label: 'OD premium' },
              { value: 'tp_premium', label: 'TP premium' },
              { value: 'net_premium', label: 'Net premium' },
              { value: 'gross_premium', label: 'Gross premium' },
              { value: 'total_premium', label: 'Total premium' },
            ]}
            onChange={(e) => setDraft({ ...draft, basis: e.target.value })}
          />
          {TEXT_FILTERS.map(({ key, label }) => (
            <Input
              key={key}
              name={`filter-${key}`}
              label={label}
              value={draft[key] ?? ''}
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && apply()}
            />
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={apply}>Apply filters</Button>
          <Button
            variant="outline"
            onClick={reset}
            disabled={activeFilterCount(draft) + activeFilterCount(applied) === 0}
          >
            Reset
          </Button>
          <Button variant="outline" onClick={exportCsv}>
            Export CSV
          </Button>
        </div>
      </Card>

      <Card>
        {rows.isLoading && <LoadingSpinner />}
        {rows.error && (
          <p role="alert" className="text-sm text-red-600">
            {getErrorMessage(rows.error)}
          </p>
        )}
        {rows.data && (
          <>
            <p className="mb-2 text-sm text-gray-500">{total} rows</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-4">Class</th>
                    <th className="py-2 pr-4">Segment</th>
                    <th className="py-2 pr-4">Policy type</th>
                    <th className="py-2 pr-4">Region</th>
                    <th className="py-2 pr-4">Rate</th>
                    <th className="py-2 pr-4">Basis</th>
                    <th className="py-2 pr-4">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.data.results.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-4">{r.lob}</td>
                      <td className="py-2 pr-4">{r.insurer_segment || r.class_group}</td>
                      <td className="py-2 pr-4">{r.policy_type || '-'}</td>
                      <td className="py-2 pr-4">{r.geo_label}</td>
                      <td className="py-2 pr-4 font-medium">{r.rate}</td>
                      <td className="py-2 pr-4">{r.premium_basis}</td>
                      <td className="py-2 pr-4 text-xs text-gray-500">{r.source_ref}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / PAGE_SIZE)}
                totalItems={total}
                itemsPerPage={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>
      <p className="mt-3 text-sm">
        <Link className="text-primary-600 hover:underline" to={`/commissions/grids/${v.grid_id}`}>
          Back to all versions
        </Link>
      </p>
    </div>
  );
}
